import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tag, Calendar, DollarSign, MessageSquare, Flag, FileText, AlertTriangle } from 'lucide-react';
import type { Annotation } from '@/types';

const typeIcons: Record<string, any> = {
  entity: Tag,
  date: Calendar,
  amount: DollarSign,
  quote: MessageSquare,
  flag: Flag,
  event: FileText,
};

const typeColors: Record<string, string> = {
  entity: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  date: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100',
  amount: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
  quote: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100',
  flag: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  event: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
};

interface AnnotationCardProps {
  annotation: Annotation;
}

export function AnnotationCard({ annotation }: AnnotationCardProps) {
  const Icon = typeIcons[annotation.annotation_type] || FileText;
  const isHighPriority = annotation.annotation_type === 'flag' || 
    (annotation.annotation_metadata?.flag_severity && 
     ['high', 'critical'].includes(annotation.annotation_metadata.flag_severity));

  return (
    <Card className={isHighPriority ? 'border-red-200 dark:border-red-800' : ''}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Icon className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={typeColors[annotation.annotation_type] || typeColors.event}>
                {annotation.annotation_type}
              </Badge>
              
              {annotation.page_number && (
                <span className="text-xs text-muted-foreground">
                  Page {annotation.page_number}
                </span>
              )}
              
              {annotation.confidence !== undefined && annotation.confidence !== null && (
                <span className="text-xs text-muted-foreground">
                  {Math.round(annotation.confidence * 100)}% confidence
                </span>
              )}
              
              {annotation.annotation_metadata?.flag_severity && (
                <Badge variant="outline" className="text-xs">
                  {annotation.annotation_metadata.flag_severity}
                </Badge>
              )}
            </div>
            
            <div className="text-sm leading-relaxed">
              {annotation.content}
            </div>
            
            {annotation.context && (
              <div className="text-xs text-muted-foreground border-l-2 border-muted pl-3 mt-2 italic">
                &ldquo;{annotation.context}&rdquo;
              </div>
            )}
            
            {annotation.annotation_metadata && Object.keys(annotation.annotation_metadata).length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2">
                {Object.entries(annotation.annotation_metadata).map(([key, value]) => {
                  if (key === 'is_summary' || key === 'source') return null;
                  return (
                    <span key={key} className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                      {key}: {String(value)}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
